import { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Plus, ShieldStar, Bank, CheckCircle, XCircle, Trash, Prohibit, PencilSimple, FastForward, Sparkle, X, MagnifyingGlass, Coins, ClockCounterClockwise } from "@phosphor-icons/react";
import ImageUploader from "../components/ImageUploader";

const CATEGORIES = ["singing", "dancing", "comedy", "rap", "modeling", "trivia", "invention"];

export default function Admin() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ title: "", category: "singing", tier: "entry", bracket_size: 32, round_hours: 24, description: "", cover_image: "", gender: "" });
    const [payouts, setPayouts] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [skip, setSkip] = useState(0);
    const [total, setTotal] = useState(0);
    const [settings, setSettings] = useState({ auto_approve_max: 0 });
    const [savingSettings, setSavingSettings] = useState(false);
    const [comps, setComps] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [tierModal, setTierModal] = useState(null); // null or { ...tier, _isNew }
    // Coin ops
    const [coinSearch, setCoinSearch] = useState("");
    const [coinResults, setCoinResults] = useState([]);
    const [coinTarget, setCoinTarget] = useState(null); // selected user for grant/deduct
    const [coinAmount, setCoinAmount] = useState("");
    const [coinReason, setCoinReason] = useState("");
    const [coinBusy, setCoinBusy] = useState(false);
    // Audit log
    const [audit, setAudit] = useState([]);
    // Gift catalog
    const [gifts, setGifts] = useState([]);
    const [giftModal, setGiftModal] = useState(null); // null or { ...gift, _isNew }
    const PAGE = 20;

    const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    const loadSettings = () => api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    const loadComps = () => api.get("/competitions").then(({ data }) => setComps(data || [])).catch(() => {});
    const loadTiers = () => api.get("/tiers").then(({ data }) => setTiers(data || [])).catch(() => {});
    const loadAudit = () => api.get("/admin/audit-log?limit=30").then(({ data }) => setAudit(data.items || [])).catch(() => {});
    const loadGifts = () => api.get("/gifts/catalog").then(({ data }) => setGifts(data || [])).catch(() => {});
    const loadPayouts = useCallback((s = 0) => {
        const url = `/admin/payouts?limit=${PAGE}&skip=${s}` + (filter ? `&status=${filter}` : "");
        api.get(url).then(({ data }) => {
            setPayouts(data.items || []);
            setTotal(data.total || 0);
            setSkip(s);
        }).catch(() => {});
    }, [filter]);

    useEffect(() => { loadStats(); loadSettings(); loadComps(); loadTiers(); loadAudit(); loadGifts(); }, []);
    useEffect(() => { loadPayouts(0); }, [loadPayouts]);

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            const { data } = await api.patch("/admin/settings", { auto_approve_max: parseFloat(settings.auto_approve_max) || 0 });
            setSettings(data);
            toast.success("Settings saved");
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setSavingSettings(false); }
    };

    const onCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const payload = { ...form, bracket_size: Number(form.bracket_size), round_hours: Number(form.round_hours) };
            if (payload.category !== "modeling") delete payload.gender;
            await api.post("/competitions", payload);
            toast.success("Competition created");
            setForm({ title: "", category: "singing", tier: "entry", bracket_size: 32, round_hours: 24, description: "", cover_image: "", gender: "" });
            loadComps();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setCreating(false); }
    };

    const deleteComp = async (c) => {
        if (!window.confirm(`Permanently delete '${c.title}'?\n\nThis only works for tournaments with NO paid entries. For tournaments with entries, use Cancel & Refund.`)) return;
        try {
            await api.delete(`/competitions/${c.id}`);
            toast.success("Tournament deleted");
            loadComps();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const cancelComp = async (c) => {
        const fee = (c.entry_fee || 0) * 100;
        const cnt = c.entries_count ?? "?";
        if (!window.confirm(`Cancel '${c.title}' and REFUND all participants?\n\nEach of the ${cnt} entrant(s) will receive ${fee} coins back. They'll be notified.\n\nThis cannot be undone.`)) return;
        try {
            const { data } = await api.post(`/competitions/${c.id}/cancel`);
            toast.success(`Cancelled. ${data.refunded_users} user(s) refunded ${data.refund_amount_per_user} coins each.`);
            loadComps();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const advanceComp = async (c) => {
        const roundNames = ["Registering", "Round of 32", "Round of 16", "Quarterfinals", "Semifinals", "Final"];
        const current = roundNames[c.current_round] || `Round ${c.current_round}`;
        const next = c.current_round >= 5 ? "Finish tournament" : (roundNames[(c.current_round || 0) + 1] || "next round");
        if (!window.confirm(`Force advance '${c.title}'?\n\nCurrent: ${current}\nNext: ${next}\n\nThis normally happens automatically every 24 h.`)) return;
        try {
            await api.post(`/competitions/${c.id}/advance`);
            toast.success(`Advanced to ${next}`);
            loadComps();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    // ===== Tier Manager =====
    const openNewTier = () => setTierModal({
        _isNew: true, slug: "", name: "", description: "", fee: 50, max_players: 32,
        platform_fee_percent: 30, color: "#4F9BFF", icon: "⭐", display_order: 99,
    });
    const openEditTier = (t) => setTierModal({ ...t, _isNew: false });

    const saveTier = async () => {
        const t = tierModal;
        try {
            if (t._isNew) {
                await api.post("/tiers", {
                    slug: t.slug, name: t.name, description: t.description,
                    fee: Number(t.fee), max_players: Number(t.max_players),
                    platform_fee_percent: Number(t.platform_fee_percent),
                    color: t.color, icon: t.icon, display_order: Number(t.display_order),
                });
                toast.success(`Tier '${t.name}' created`);
            } else {
                await api.patch(`/tiers/${t.id}`, {
                    name: t.name, description: t.description,
                    fee: Number(t.fee), max_players: Number(t.max_players),
                    platform_fee_percent: Number(t.platform_fee_percent),
                    color: t.color, icon: t.icon, display_order: Number(t.display_order),
                });
                toast.success(`Tier '${t.name}' updated`);
            }
            setTierModal(null);
            loadTiers();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const deleteTier = async (t) => {
        if (t.is_default) return toast.error("Default tiers can't be deleted — edit them instead");
        if (!window.confirm(`Delete tier '${t.name}' (${t.slug})?\n\nThis only works if no active tournaments are using it.`)) return;
        try {
            await api.delete(`/tiers/${t.id}`);
            toast.success("Tier deleted");
            loadTiers();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    // ===== Gift Catalog Manager =====
    const openNewGift = () => setGiftModal({
        _isNew: true, slug: "", name: "", emoji: "🎁", image_url: "",
        cost: 10, animation: "float-up", color: "#F59E0B", display_order: 99,
    });
    const openEditGift = (g) => setGiftModal({ ...g, _isNew: false });

    const saveGift = async () => {
        const g = giftModal;
        if (!g.name?.trim()) return toast.error("Name is required");
        if (!g.emoji && !g.image_url) return toast.error("Either emoji or image URL is required");
        if (Number(g.cost) < 1) return toast.error("Cost must be at least 1 coin");
        try {
            if (g._isNew) {
                if (!g.slug?.trim()) return toast.error("Slug is required");
                await api.post("/admin/gifts", {
                    slug: g.slug, name: g.name, emoji: g.emoji || "", image_url: g.image_url || "",
                    cost: Number(g.cost), animation: g.animation, color: g.color,
                    display_order: Number(g.display_order),
                });
                toast.success(`Gift '${g.name}' created`);
            } else {
                await api.patch(`/admin/gifts/${g.id}`, {
                    name: g.name, emoji: g.emoji || "", image_url: g.image_url || "",
                    cost: Number(g.cost), animation: g.animation, color: g.color,
                    display_order: Number(g.display_order),
                });
                toast.success(`Gift '${g.name}' updated`);
            }
            setGiftModal(null);
            loadGifts();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const deleteGift = async (g) => {
        if (g.is_default) return toast.error("Default gifts can't be deleted — edit them instead");
        if (!window.confirm(`Delete gift '${g.name}' (${g.slug})?`)) return;
        try {
            await api.delete(`/admin/gifts/${g.id}`);
            toast.success("Gift deleted");
            loadGifts();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const approve = async (id) => {
        try {
            await api.post(`/admin/payouts/${id}/approve`);
            toast.success("Payout marked as paid");
            loadPayouts(skip);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };
    const reject = async (id) => {
        if (!confirm("Reject this payout and refund the user's earnings?")) return;
        try {
            const { data } = await api.post(`/admin/payouts/${id}/reject`);
            toast.success(`Refunded $${data.refunded.toFixed(2)} to user`);
            loadPayouts(skip);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-5xl mx-auto">
            <div className="font-heading text-xs tracking-[0.4em] text-wta-muted">PLATFORM CONTROL</div>
            <h1 className="font-heading text-5xl md:text-6xl tracking-tight uppercase flex items-center gap-3">
                <ShieldStar size={48} weight="fill" className="text-wta-primary" /> Admin
            </h1>
            <p className="text-wta-muted mt-2">Welcome, {user?.name}.</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
                <Stat label="Users" value={stats?.users} />
                <Stat label="Competitions" value={stats?.competitions} />
                <Stat label="Active" value={stats?.active_competitions} />
                <Stat label="Videos" value={stats?.videos} />
            </div>

            <div className="bg-wta-surface border border-wta-border p-6 rounded-sm mt-8">
                <div className="font-heading text-2xl tracking-wider mb-1">Platform Settings</div>
                <p className="text-xs text-wta-muted mb-4">Tune automation thresholds.</p>
                <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                    <div>
                        <label className="text-xs tracking-widest text-wta-muted uppercase">Auto-approve payout threshold (USD)</label>
                        <input
                            data-testid="setting-auto-approve"
                            type="number" min="0" step="0.01"
                            value={settings.auto_approve_max ?? 0}
                            onChange={(e) => setSettings({ ...settings, auto_approve_max: e.target.value })}
                            className="mt-1 w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                        />
                        <div className="text-[10px] text-wta-muted mt-1">Payouts at or below this amount auto-mark as paid (skip the manual queue). Set to 0 to disable.</div>
                    </div>
                    <button data-testid="save-settings" disabled={savingSettings} onClick={saveSettings} className="btn-primary px-5 py-2 rounded-sm text-sm disabled:opacity-50">
                        {savingSettings ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            {/* Live Appeals Queue */}
            <LiveAppealsAdmin />

            {/* Payout Queue */}
            <div className="bg-wta-surface border border-wta-border p-6 rounded-sm mt-8">
                <div className="flex items-center justify-between mb-4">
                    <div className="font-heading text-2xl tracking-wider flex items-center gap-2">
                        <Bank size={20} /> Payout Queue
                    </div>
                    <div className="flex gap-1">
                        {["pending", "completed", "rejected", ""].map((s) => (
                            <button
                                key={s || "all"}
                                data-testid={`payout-filter-${s || "all"}`}
                                onClick={() => setFilter(s)}
                                className={`px-3 py-1 rounded-sm border text-xs font-heading uppercase tracking-wider ${filter === s ? "bg-wta-primary border-wta-primary" : "border-wta-border hover:border-white text-white/70"}`}
                            >
                                {s || "All"}
                            </button>
                        ))}
                    </div>
                </div>
                {payouts.length === 0 ? (
                    <div className="text-wta-muted text-sm text-center py-6">No {filter || ""} payout requests</div>
                ) : (
                    <>
                        <div className="space-y-2">
                            {payouts.map((p) => (
                                <div key={p.id} data-testid={`payout-row-${p.id}`} className="bg-wta-surface2 border border-wta-border rounded-sm p-3 grid md:grid-cols-[1fr_auto_auto] gap-3 items-center">
                                    <div className="min-w-0">
                                        <div className="text-sm">@{p.user?.name || p.user_id}</div>
                                        <div className="text-xs text-wta-muted">
                                            {p.payout_method || "manual"} · {p.payout_destination || "—"}
                                        </div>
                                        {p.note && <div className="text-xs text-white/60 mt-1 italic">"{p.note}"</div>}
                                        <div className="text-[10px] text-wta-muted mt-1">{new Date(p.created_at).toLocaleString()} · {p.status}</div>
                                    </div>
                                    <div className="font-heading text-3xl text-wta-accent">${p.amount.toFixed(2)}</div>
                                    {p.status === "pending" ? (
                                        <div className="flex gap-2">
                                            <button data-testid={`approve-${p.id}`} onClick={() => approve(p.id)} className="btn-primary px-3 py-2 rounded-sm text-xs flex items-center gap-1">
                                                <CheckCircle size={14} weight="fill" /> Mark Paid
                                            </button>
                                            <button data-testid={`reject-${p.id}`} onClick={() => reject(p.id)} className="btn-ghost px-3 py-2 rounded-sm text-xs font-heading uppercase tracking-wider flex items-center gap-1">
                                                <XCircle size={14} /> Reject
                                            </button>
                                        </div>
                                    ) : (
                                        <div className={`text-xs font-heading uppercase tracking-wider ${p.status === "completed" ? "text-wta-accent" : "text-wta-primary"}`}>
                                            {p.status}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {total > PAGE && (
                            <div className="flex items-center justify-between mt-4 text-sm">
                                <div className="text-wta-muted">{skip + 1}–{Math.min(skip + PAGE, total)} of {total}</div>
                                <div className="flex gap-2">
                                    <button data-testid="payout-prev" disabled={skip === 0} onClick={() => loadPayouts(Math.max(0, skip - PAGE))} className="btn-ghost px-3 py-1.5 rounded-sm text-xs font-heading uppercase tracking-wider disabled:opacity-40">Prev</button>
                                    <button data-testid="payout-next" disabled={skip + PAGE >= total} onClick={() => loadPayouts(skip + PAGE)} className="btn-ghost px-3 py-1.5 rounded-sm text-xs font-heading uppercase tracking-wider disabled:opacity-40">Next</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Create competition */}
            <div className="bg-wta-surface border border-wta-border p-6 rounded-sm mt-8">
                <div className="font-heading text-2xl tracking-wider mb-4 flex items-center gap-2">
                    <Plus size={20} /> Create Competition
                </div>
                <form onSubmit={onCreate} className="grid md:grid-cols-2 gap-3">
                    <input data-testid="admin-comp-title" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="md:col-span-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                    <select data-testid="admin-comp-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm">
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select data-testid="admin-comp-tier" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm">
                        {tiers.map((t) => <option key={t.slug} value={t.slug}>{t.icon} {t.name} — {t.fee} coins (${(t.fee/100).toFixed(2)})</option>)}
                    </select>
                    <select data-testid="admin-comp-bracket-size" value={form.bracket_size} onChange={(e) => setForm({ ...form, bracket_size: Number(e.target.value) })} className="bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm">
                        <option value={8}>8 players (3 rounds)</option>
                        <option value={16}>16 players (4 rounds)</option>
                        <option value={32}>32 players (5 rounds)</option>
                    </select>
                    <select data-testid="admin-comp-round-hours" value={form.round_hours} onChange={(e) => setForm({ ...form, round_hours: Number(e.target.value) })} className="bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm">
                        <option value={1}>Round = 1 hour ⚡ (fast)</option>
                        <option value={2}>Round = 2 hours</option>
                        <option value={3}>Round = 3 hours</option>
                        <option value={4}>Round = 4 hours</option>
                        <option value={24}>Round = 24 hours (default)</option>
                    </select>
                    {form.category === "modeling" && (
                        <select data-testid="admin-comp-gender" value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="md:col-span-2 bg-wta-surface2 border border-wta-primary/40 rounded-sm px-3 py-2.5 text-sm">
                            <option value="">Select gender (required for modeling)</option>
                            <option value="female">Female models only</option>
                            <option value="male">Male models only</option>
                        </select>
                    )}
                    <input data-testid="admin-comp-cover" placeholder="Cover image URL (or upload below)" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="md:col-span-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                    <div className="md:col-span-2">
                        <label className="text-xs tracking-widest text-wta-muted uppercase block mb-2">Cover image (16:9)</label>
                        <ImageUploader
                            value={form.cover_image}
                            onChange={(url) => setForm({ ...form, cover_image: url })}
                            aspect={16 / 9}
                            cropShape="rect"
                            outputSize={{ width: 1280, height: 720 }}
                            label="Upload Cover"
                            testid="admin-cover"
                            previewClass="w-32 aspect-video rounded-sm"
                            placeholder="—"
                        />
                    </div>
                    <textarea data-testid="admin-comp-desc" placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2.5 text-sm" />
                    <button data-testid="admin-comp-submit" disabled={creating} className="md:col-span-2 btn-primary py-3 rounded-sm text-base disabled:opacity-50">
                        {creating ? "Creating…" : "Create"}
                    </button>
                </form>
            </div>

            {/* Existing tournaments — manage */}
            <div className="bg-wta-surface border border-wta-border rounded-sm mt-8">
                <div className="px-5 py-4 border-b border-wta-border flex items-center justify-between">
                    <div>
                        <div className="text-xs tracking-widest text-wta-muted uppercase">Manage</div>
                        <h2 className="font-heading text-2xl uppercase tracking-tight">Tournaments ({comps.length})</h2>
                    </div>
                    <button onClick={loadComps} className="btn-ghost text-xs px-3 py-1.5 rounded-sm">Refresh</button>
                </div>
                <div className="divide-y divide-wta-border">
                    {comps.length === 0 ? (
                        <div className="px-5 py-6 text-sm text-wta-muted">No tournaments yet.</div>
                    ) : comps.map((c) => {
                        const fee = (c.entry_fee || 0) * 100;
                        const status = c.status || "registering";
                        const cancelled = status === "cancelled";
                        const finished = status === "finished";
                        const cnt = c.entries_count ?? 0;
                        return (
                            <div key={c.id} data-testid={`admin-comp-${c.id}`} className="px-5 py-4 flex items-center gap-3 flex-wrap">
                                {c.cover_image && <img src={c.cover_image} alt="" className="w-14 h-14 rounded-sm object-cover shrink-0" />}
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading text-base truncate">{c.title}</div>
                                    <div className="text-xs text-wta-muted mt-1 flex flex-wrap gap-2">
                                        <span className="uppercase tracking-wider">{c.category}</span>
                                        <span>·</span>
                                        <span className="uppercase tracking-wider">{c.tier}</span>
                                        <span>·</span>
                                        <span>{cnt}/{c.max_participants || 32} entries</span>
                                        <span>·</span>
                                        <span>{fee} coins fee</span>
                                        <span>·</span>
                                        <span className={`uppercase tracking-wider font-heading ${cancelled ? "text-red-400" : finished ? "text-wta-muted" : "text-wta-primary"}`}>{status}</span>
                                        <span>·</span>
                                        <span>round = {c.round_hours || 24}h</span>
                                    </div>
                                </div>
                                <div className="flex gap-2 shrink-0 items-center">
                                    {!cancelled && !finished && (
                                        <select
                                            data-testid={`admin-comp-round-hours-${c.id}`}
                                            value={c.round_hours || 24}
                                            onChange={async (e) => {
                                                const hours = Number(e.target.value);
                                                try {
                                                    await api.patch(`/competitions/${c.id}`, { round_hours: hours });
                                                    toast.success(`Round timer set to ${hours}h`);
                                                    loadComps();
                                                } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
                                            }}
                                            className="bg-wta-surface2 border border-wta-border rounded-sm px-2 py-1 text-xs cursor-pointer hover:border-wta-primary"
                                            title="Change round duration"
                                        >
                                            <option value={1}>1h ⚡</option>
                                            <option value={2}>2h</option>
                                            <option value={3}>3h</option>
                                            <option value={4}>4h</option>
                                            <option value={24}>24h</option>
                                        </select>
                                    )}                                    {cnt > 0 && !cancelled && !finished && (
                                        <button
                                            onClick={() => advanceComp(c)}
                                            data-testid={`admin-comp-advance-${c.id}`}
                                            className="btn-ghost text-xs px-3 py-2 rounded-sm flex items-center gap-1 text-wta-primary hover:text-wta-primary/80"
                                            title="Force advance to next round"
                                        >
                                            <FastForward size={14} weight="bold" /> Advance
                                        </button>
                                    )}
                                    {cnt === 0 && !cancelled && !finished && (
                                        <button
                                            onClick={() => deleteComp(c)}
                                            data-testid={`admin-comp-delete-${c.id}`}
                                            className="btn-ghost text-xs px-3 py-2 rounded-sm flex items-center gap-1 text-red-400 hover:text-red-300"
                                            title="Permanently delete (no entries)"
                                        >
                                            <Trash size={14} weight="bold" /> Delete
                                        </button>
                                    )}
                                    {cnt > 0 && !cancelled && !finished && (
                                        <button
                                            onClick={() => cancelComp(c)}
                                            data-testid={`admin-comp-cancel-${c.id}`}
                                            className="btn-ghost text-xs px-3 py-2 rounded-sm flex items-center gap-1 text-yellow-400 hover:text-yellow-300"
                                            title="Cancel & refund all participants"
                                        >
                                            <Prohibit size={14} weight="bold" /> Cancel & Refund
                                        </button>
                                    )}
                                    {(cancelled || finished) && (
                                        <span className="text-[10px] tracking-widest uppercase text-wta-muted px-2 py-2">Read-only</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Tier Manager */}
            <div className="bg-wta-surface border border-wta-border rounded-sm mt-8">
                <div className="px-5 py-4 border-b border-wta-border flex items-center justify-between">
                    <div>
                        <div className="text-xs tracking-widest text-wta-muted uppercase">Configure</div>
                        <h2 className="font-heading text-2xl uppercase tracking-tight">Tiers ({tiers.length})</h2>
                    </div>
                    <button
                        onClick={openNewTier}
                        data-testid="admin-tier-new"
                        className="btn-primary text-xs px-3 py-2 rounded-sm flex items-center gap-1"
                    >
                        <Plus size={14} weight="bold" /> New Tier
                    </button>
                </div>
                <div className="divide-y divide-wta-border">
                    {tiers.length === 0 ? (
                        <div className="px-5 py-6 text-sm text-wta-muted">No tiers — create one or wait for defaults to seed.</div>
                    ) : tiers.map((t) => (
                        <div key={t.id} data-testid={`admin-tier-${t.slug}`} className="px-5 py-3 flex items-center gap-3">
                            <div
                                className="w-10 h-10 rounded-sm flex items-center justify-center text-xl shrink-0"
                                style={{ background: `${t.color}20`, border: `1px solid ${t.color}40` }}
                                title={t.color}
                            >
                                {t.icon || "⭐"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <div className="font-heading text-base">{t.name}</div>
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted font-mono">{t.slug}</span>
                                    {t.is_default && <span className="text-[9px] tracking-widest uppercase text-wta-accent bg-wta-accent/15 px-1.5 py-0.5 rounded-sm"><Sparkle size={10} weight="fill" className="inline -mt-0.5" /> default</span>}
                                </div>
                                <div className="text-xs text-wta-muted mt-0.5">
                                    <span className="font-bold text-white">{t.fee} coins</span> fee · {t.max_players} players · {t.platform_fee_percent}% platform cut
                                    {t.description && <> · {t.description}</>}
                                </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <button
                                    onClick={() => openEditTier(t)}
                                    data-testid={`admin-tier-edit-${t.slug}`}
                                    className="btn-ghost text-xs px-3 py-2 rounded-sm flex items-center gap-1"
                                    title="Edit tier"
                                >
                                    <PencilSimple size={14} weight="bold" /> Edit
                                </button>
                                {!t.is_default && (
                                    <button
                                        onClick={() => deleteTier(t)}
                                        data-testid={`admin-tier-delete-${t.slug}`}
                                        className="btn-ghost text-xs px-3 py-2 rounded-sm flex items-center gap-1 text-red-400 hover:text-red-300"
                                        title="Delete"
                                    >
                                        <Trash size={14} weight="bold" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ============================ Gift Catalog ============================ */}
            <div className="bg-wta-surface border border-wta-border p-6 rounded-sm mt-8">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="font-heading text-2xl tracking-wider flex items-center gap-2">
                            <Sparkle size={20} weight="fill" className="text-wta-accent" /> Gift Catalog
                        </div>
                        <p className="text-xs text-wta-muted mt-1">Animated gifts sent by viewers. Edit emoji, cost, color, and animation style.</p>
                    </div>
                    <button
                        data-testid="admin-gift-new"
                        onClick={openNewGift}
                        className="btn-primary px-4 py-2 rounded-sm text-xs font-heading tracking-wider uppercase flex items-center gap-1"
                    >
                        <Plus size={14} weight="bold" /> New Gift
                    </button>
                </div>
                {gifts.length === 0 ? (
                    <div className="text-wta-muted text-sm">No gifts yet. Click "New Gift" to add one.</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                        {gifts.map((g) => (
                            <div
                                key={g.id}
                                data-testid={`admin-gift-${g.slug}`}
                                className="bg-wta-surface2 border rounded-sm p-3 flex flex-col items-center gap-2"
                                style={{ borderColor: `${g.color}55` }}
                            >
                                <div
                                    className="text-4xl h-14 flex items-center justify-center"
                                    style={{ filter: `drop-shadow(0 0 12px ${g.color}99)` }}
                                >
                                    {g.image_url ? (
                                        <img src={g.image_url} alt="" className="w-12 h-12 object-contain" />
                                    ) : (
                                        <span>{g.emoji || "🎁"}</span>
                                    )}
                                </div>
                                <div className="font-heading text-sm tracking-wider text-center">
                                    {g.name}
                                    {g.is_default && (
                                        <span className="ml-1.5 px-1.5 py-0.5 text-[8px] bg-wta-accent/20 text-wta-accent rounded font-normal tracking-widest">DEFAULT</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-wta-accent font-heading">
                                    <Coins size={12} weight="fill" /> {g.cost}
                                </div>
                                <div className="text-[10px] tracking-widest uppercase text-wta-muted">{g.animation}</div>
                                <div className="flex gap-1 mt-1 w-full">
                                    <button
                                        onClick={() => openEditGift(g)}
                                        data-testid={`admin-gift-edit-${g.slug}`}
                                        className="flex-1 btn-ghost text-xs px-2 py-1.5 rounded-sm flex items-center justify-center gap-1"
                                    >
                                        <PencilSimple size={12} weight="bold" /> Edit
                                    </button>
                                    {!g.is_default && (
                                        <button
                                            onClick={() => deleteGift(g)}
                                            data-testid={`admin-gift-delete-${g.slug}`}
                                            className="btn-ghost text-xs px-2 py-1.5 rounded-sm text-red-400 hover:text-red-300"
                                            title="Delete"
                                        >
                                            <Trash size={12} weight="bold" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Gift edit/create modal */}
            {giftModal && (
                <div data-testid="gift-modal" className="fixed inset-0 z-[90] bg-black/75 flex items-center justify-center p-4" onClick={() => setGiftModal(null)}>
                    <div className="bg-wta-surface border border-wta-border rounded-sm max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading text-xl uppercase tracking-tight">
                                {giftModal._isNew ? "New Gift" : `Edit ${giftModal.name}`}
                            </h3>
                            <button onClick={() => setGiftModal(null)} className="text-wta-muted hover:text-white"><X size={18} /></button>
                        </div>
                        {/* Preview */}
                        <div
                            className="rounded-sm p-4 flex items-center justify-center gap-3 mb-4 border"
                            style={{
                                borderColor: `${giftModal.color}66`,
                                background: `linear-gradient(135deg, ${giftModal.color}22, transparent)`,
                            }}
                        >
                            <div className="text-5xl" style={{ filter: `drop-shadow(0 0 16px ${giftModal.color})` }}>
                                {giftModal.image_url ? (
                                    <img src={giftModal.image_url} alt="" className="w-16 h-16 object-contain" />
                                ) : (
                                    <span>{giftModal.emoji || "🎁"}</span>
                                )}
                            </div>
                            <div>
                                <div className="font-heading text-lg tracking-wider">{giftModal.name || "Gift"}</div>
                                <div className="flex items-center gap-1 text-xs text-wta-accent">
                                    <Coins size={12} weight="fill" /> {giftModal.cost} · <span className="uppercase tracking-widest text-wta-muted">{giftModal.animation}</span>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {giftModal._isNew && (
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Slug (lowercase, a-z/0-9/-/_)</span>
                                    <input
                                        data-testid="gift-slug" type="text" value={giftModal.slug}
                                        onChange={(e) => setGiftModal({ ...giftModal, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm font-mono"
                                        placeholder="e.g. star"
                                    />
                                </label>
                            )}
                            <label className="block">
                                <span className="text-[10px] tracking-widest uppercase text-wta-muted">Display Name</span>
                                <input
                                    data-testid="gift-name" type="text" value={giftModal.name}
                                    onChange={(e) => setGiftModal({ ...giftModal, name: e.target.value })}
                                    className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm"
                                />
                            </label>
                            <div className="grid grid-cols-[auto_1fr] gap-2">
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Emoji</span>
                                    <input
                                        data-testid="gift-emoji" type="text" maxLength="4" value={giftModal.emoji || ""}
                                        onChange={(e) => setGiftModal({ ...giftModal, emoji: e.target.value })}
                                        className="w-16 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-lg text-center"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Image URL (optional, overrides emoji)</span>
                                    <input
                                        data-testid="gift-image-url" type="url" value={giftModal.image_url || ""}
                                        onChange={(e) => setGiftModal({ ...giftModal, image_url: e.target.value })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm"
                                        placeholder="https://…"
                                    />
                                </label>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Cost (coins)</span>
                                    <input
                                        data-testid="gift-cost" type="number" min="1" value={giftModal.cost}
                                        onChange={(e) => setGiftModal({ ...giftModal, cost: e.target.value })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Color</span>
                                    <input
                                        data-testid="gift-color" type="color" value={giftModal.color || "#F59E0B"}
                                        onChange={(e) => setGiftModal({ ...giftModal, color: e.target.value })}
                                        className="w-full h-[38px] bg-wta-surface2 border border-wta-border rounded-sm px-1 mt-1 cursor-pointer"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Order</span>
                                    <input
                                        data-testid="gift-order" type="number" min="1" value={giftModal.display_order || 99}
                                        onChange={(e) => setGiftModal({ ...giftModal, display_order: e.target.value })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm"
                                    />
                                </label>
                            </div>
                            <label className="block">
                                <span className="text-[10px] tracking-widest uppercase text-wta-muted">Animation Style</span>
                                <select
                                    data-testid="gift-animation" value={giftModal.animation || "float-up"}
                                    onChange={(e) => setGiftModal({ ...giftModal, animation: e.target.value })}
                                    className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm"
                                >
                                    <option value="float-up">float-up — drifts up & fades (roses)</option>
                                    <option value="burst">burst — explodes in, shrinks out (hearts)</option>
                                    <option value="spin">spin — rotates while rising (stars)</option>
                                    <option value="beam">beam — radial rays pulse (crowns)</option>
                                    <option value="fireworks">fireworks — rocket launch + particles</option>
                                    <option value="confetti">confetti — diamond shower across screen</option>
                                </select>
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button onClick={() => setGiftModal(null)} className="flex-1 btn-ghost py-2.5 rounded-sm text-sm">Cancel</button>
                                <button onClick={saveGift} data-testid="gift-save" className="flex-1 btn-primary py-2.5 rounded-sm text-sm">
                                    {giftModal._isNew ? "Create Gift" : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tier edit/create modal */}
            {tierModal && (
                <div data-testid="tier-modal" className="fixed inset-0 z-[90] bg-black/75 flex items-center justify-center p-4" onClick={() => setTierModal(null)}>
                    <div className="bg-wta-surface border border-wta-border rounded-sm max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading text-xl uppercase tracking-tight">{tierModal._isNew ? "New Tier" : `Edit ${tierModal.name}`}</h3>
                            <button onClick={() => setTierModal(null)} className="text-wta-muted hover:text-white"><X size={18} /></button>
                        </div>
                        <div className="space-y-3">
                            {tierModal._isNew && (
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Slug (lowercase, a-z/0-9/-/_)</span>
                                    <input
                                        data-testid="tier-slug" type="text" value={tierModal.slug}
                                        onChange={(e) => setTierModal({ ...tierModal, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm font-mono"
                                        placeholder="e.g. bronze"
                                    />
                                </label>
                            )}
                            <label className="block">
                                <span className="text-[10px] tracking-widest uppercase text-wta-muted">Display Name</span>
                                <input data-testid="tier-name" type="text" value={tierModal.name}
                                    onChange={(e) => setTierModal({ ...tierModal, name: e.target.value })}
                                    className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm" />
                            </label>
                            <label className="block">
                                <span className="text-[10px] tracking-widest uppercase text-wta-muted">Description</span>
                                <input data-testid="tier-description" type="text" value={tierModal.description || ""}
                                    onChange={(e) => setTierModal({ ...tierModal, description: e.target.value })}
                                    className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm" />
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Entry Fee (coins)</span>
                                    <input data-testid="tier-fee" type="number" min="0" value={tierModal.fee}
                                        onChange={(e) => setTierModal({ ...tierModal, fee: e.target.value })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm" />
                                    <span className="text-[10px] text-wta-muted mt-1 block">
                                        ≈ ${(Number(tierModal.fee || 0) / 100).toFixed(2)} <span className="opacity-60">(100 coins = $1)</span>
                                    </span>
                                </label>
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Bracket Size</span>
                                    <select
                                        data-testid="tier-max-players"
                                        value={tierModal.max_players}
                                        onChange={(e) => setTierModal({ ...tierModal, max_players: Number(e.target.value) })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm"
                                    >
                                        <option value={8}>8 players (3 rounds)</option>
                                        <option value={16}>16 players (4 rounds)</option>
                                        <option value={32}>32 players (5 rounds)</option>
                                        <option value={64}>64 players (6 rounds)</option>
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Platform Cut %</span>
                                    <input data-testid="tier-split" type="number" min="0" max="100" value={tierModal.platform_fee_percent}
                                        onChange={(e) => setTierModal({ ...tierModal, platform_fee_percent: e.target.value })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm" />
                                </label>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Icon (emoji)</span>
                                    <input data-testid="tier-icon" type="text" maxLength="4" value={tierModal.icon || ""}
                                        onChange={(e) => setTierModal({ ...tierModal, icon: e.target.value })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm" />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Color</span>
                                    <input data-testid="tier-color" type="color" value={tierModal.color || "#4F9BFF"}
                                        onChange={(e) => setTierModal({ ...tierModal, color: e.target.value })}
                                        className="w-full h-[38px] bg-wta-surface2 border border-wta-border rounded-sm px-1 mt-1 cursor-pointer" />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] tracking-widest uppercase text-wta-muted">Order</span>
                                    <input data-testid="tier-order" type="number" min="1" value={tierModal.display_order || 99}
                                        onChange={(e) => setTierModal({ ...tierModal, display_order: e.target.value })}
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 mt-1 text-sm" />
                                </label>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button onClick={() => setTierModal(null)} className="flex-1 btn-ghost py-2.5 rounded-sm text-sm">Cancel</button>
                                <button onClick={saveTier} data-testid="tier-save" className="flex-1 btn-primary py-2.5 rounded-sm text-sm">
                                    {tierModal._isNew ? "Create Tier" : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-wta-surface border border-wta-border p-4 rounded-sm">
            <div className="text-xs tracking-widest uppercase text-wta-muted">{label}</div>
            <div className="font-heading text-4xl mt-1">{value ?? "—"}</div>
        </div>
    );
}

function LiveAppealsAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/live-appeals?status=pending")
            .then(({ data }) => setItems(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const approve = async (id) => {
        try {
            await api.post(`/admin/live-appeals/${id}/approve`);
            toast.success("Live access restored");
            load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };
    const reject = async (id) => {
        try {
            await api.post(`/admin/live-appeals/${id}/reject`);
            toast.success("Appeal rejected");
            load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div data-testid="admin-live-appeals" className="bg-wta-surface border border-wta-border p-6 rounded-sm mt-8">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="font-heading text-2xl tracking-wider">Live Streaming Appeals</div>
                    <p className="text-xs text-wta-muted">Users requesting their live privileges to be reinstated.</p>
                </div>
                <span className="px-2 py-1 bg-wta-primary/20 text-wta-primary text-xs font-heading uppercase tracking-widest rounded-sm">{items.length} pending</span>
            </div>
            {loading ? <div className="text-wta-muted text-sm">Loading…</div> : items.length === 0 ? (
                <div className="text-wta-muted text-sm">No pending appeals.</div>
            ) : (
                <div className="space-y-2">
                    {items.map((a) => (
                        <div key={a.id} data-testid={`appeal-row-${a.id}`} className="bg-wta-surface2 border border-wta-border rounded-sm p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <div className="font-heading text-lg tracking-wide">@{a.user_name}</div>
                                    <div className="text-xs text-wta-muted">Submitted {new Date(a.created_at).toLocaleString()}</div>
                                    <div className="text-sm mt-2 text-white/90">{a.message}</div>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button data-testid={`appeal-approve-${a.id}`} onClick={() => approve(a.id)} className="btn-primary px-4 py-2 rounded-sm text-xs">Approve</button>
                                    <button data-testid={`appeal-reject-${a.id}`} onClick={() => reject(a.id)} className="btn-ghost px-4 py-2 rounded-sm text-xs">Reject</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

